"use client";

import React from "react";
import Cookies from "js-cookie";
import { WhiteLabelWithParameters } from "@/interfaces/api";
import LicenseClient from "@/clients/license.client";

interface IProps {
	children: React.ReactNode;
	whitelabel: WhiteLabelWithParameters;
	session: ILicenseSession|null;
}

interface IGuessContext {
	whitelabel: WhiteLabelWithParameters;
	session: ILicenseSession|null;
	setSession(user: ILicenseSession): void;
	endSession(): void;
	apiClient: LicenseClient;
}

interface ILoggedContext extends IGuessContext {
	session: ILicenseSession;
}

const LicenseContext = React.createContext<IGuessContext | null>(null);

const LicenseContextProvider = (props: IProps) => {
	const [licenseSession, setLicenseSession] = React.useState<ILicenseSession|null>(props.session);

	const cookieName = React.useMemo(() => {
		return 'auth-wl-' + props.whitelabel.identifier;
	}, [props.whitelabel.identifier]);

	const cookie = React.useMemo(() => {
		return Cookies.get(cookieName);
	}, [cookieName]);

	const setSession = React.useCallback((session: ILicenseSession) => {
		setLicenseSession(session);
		Cookies.set(cookieName, session.token);
	}, [cookieName]);

	const endSession = React.useCallback(() => {
		setLicenseSession(null);
		Cookies.remove(cookieName);
	}, [cookieName]);

	const apiClient = React.useMemo(() => {
		return new LicenseClient(cookie, props.whitelabel.identifier)
	}, [cookie, props.whitelabel.identifier]);

	return (
		<LicenseContext.Provider
			value={{
				whitelabel: props.whitelabel,
				session: licenseSession,
				setSession: setSession,
				endSession: endSession,
				apiClient: apiClient,
			}}
		>
			{props.children}
		</LicenseContext.Provider>
	);
};

const useLicenseContext = () => {
	const context = React.useContext(LicenseContext);

	return context as IGuessContext;
};

const useLoggedLicenseContext = () => {
	const context = React.useContext(LicenseContext);

	return context as ILoggedContext;
};

export { LicenseContext, useLicenseContext, useLoggedLicenseContext, LicenseContextProvider };
